import { User } from '../../models/User';
import { Organization, OrganizationType } from '../../models/Organization';
import { Booking } from '../../types/booking';
import { UserRole } from '../../types/user';
import { PermissionResult, explain } from './result';
import { getRolePermissions } from '../../features/shared/permissions/permissions';

/**
 * Centralized permission utilities for booking operations.
 * All functions are pure and depend only on user role, organization type, and booking data.
 */

/**
 * Check if a user can create a booking in the given organization.
 * Derived from the org-type permission matrix — change the matrix entry to change this behaviour.
 */
export function canCreateBooking(user: User | null, org: Organization | null): boolean {
  if (!user || !org) return false;
  return getRolePermissions(org.type as OrganizationType, user.role as UserRole).includes('BOOKINGS:CREATE');
}

/**
 * Check if a user can edit a specific booking.
 * Roles with BOOKINGS:DISPATCH_OVERRIDE can edit any booking;
 * roles with only BOOKINGS:UPDATE can edit their own (CFI also covers bookings where they are instructor).
 */
export function canEditBooking(user: User | null, booking: Booking | null, org: Organization | null): boolean {
  if (!user || !booking || !org) return false;
  const { uid } = user;
  const perms = getRolePermissions(org.type as OrganizationType, user.role as UserRole);
  if (!perms.includes('BOOKINGS:UPDATE')) return false;
  if (perms.includes('BOOKINGS:DISPATCH_OVERRIDE')) return true;
  if (user.role === 'cfi') return booking.userId === uid || booking.instructorId === uid;
  return booking.userId === uid;
}

/**
 * Check if a user can cancel a specific booking.
 * BOOKINGS:CANCEL_OTHERS → cancel any; BOOKINGS:DELETE → cancel own only.
 */
export function canCancelBooking(user: User | null, booking: Booking | null, org: Organization | null): boolean {
  if (!user || !booking || !org) return false;
  const perms = getRolePermissions(org.type as OrganizationType, user.role as UserRole);
  if (perms.includes('BOOKINGS:CANCEL_OTHERS')) return true;
  if (perms.includes('BOOKINGS:DELETE')) return booking.userId === user.uid;
  return false;
}

/**
 * Check if a user can complete a specific booking.
 * BOOKINGS:DISPATCH_OVERRIDE → complete any; BOOKINGS:UPDATE → complete own/involved.
 * Students have BOOKINGS:READ only but can still complete their own bookings (lifecycle rule).
 */
export function canCompleteBooking(user: User | null, booking: Booking | null, org: Organization | null): boolean {
  if (!user || !booking || !org) return false;
  const { uid } = user;
  const perms = getRolePermissions(org.type as OrganizationType, user.role as UserRole);
  if (perms.includes('BOOKINGS:DISPATCH_OVERRIDE')) return true;
  if (perms.includes('BOOKINGS:UPDATE')) {
    if (user.role === 'cfi') return booking.userId === uid || booking.instructorId === uid;
    return booking.userId === uid;
  }
  // Students can complete their own booked flights even though they cannot edit them.
  if (user.role === 'student') return booking.userId === uid;
  return false;
}

/**
 * Check if a user can view a specific booking.
 * BOOKINGS:DISPATCH_OVERRIDE or BOOKINGS:CANCEL_OTHERS → view any.
 * Maintenance also sees all bookings for aircraft-coordination purposes (BOOKINGS:READ only).
 */
export function canViewBooking(user: User | null, booking: Booking | null, org: Organization | null): boolean {
  if (!user || !booking || !org) return false;
  const { uid } = user;
  const perms = getRolePermissions(org.type as OrganizationType, user.role as UserRole);
  if (!perms.includes('BOOKINGS:READ')) return false;
  if (perms.includes('BOOKINGS:DISPATCH_OVERRIDE') || perms.includes('BOOKINGS:CANCEL_OTHERS')) return true;
  // Maintenance views all bookings for aircraft coordination but has no cancel/dispatch power.
  // Remove AIRCRAFT:UPDATE from club/school maintenance matrix to restrict this if needed.
  if (perms.includes('AIRCRAFT:UPDATE')) return true;
  if (org.type === 'club' && user.role === 'member' && org.settings?.bookingVisibility?.showMemberNames !== false) {
    return true;
  }
  if (user.role === 'cfi') return booking.userId === uid || booking.instructorId === uid || booking.studentId === uid;
  return booking.userId === uid || booking.pilotId === uid;
}

// ===== BOOKING VISIBILITY UTILITIES =====

/**
 * Detailed visibility result for booking rendering.
 * This determines what information should be shown to the user.
 */
export interface BookingVisibilityDetails {
  /** Can the user see this booking at all? */
  canView: boolean;
  /** Can the user see full details (pilot name, notes, times, etc.)? */
  showFullDetails: boolean;
  /** Can the user edit this booking? */
  canEdit: boolean;
  /** Can the user cancel/delete this booking? */
  canCancel: boolean;
  /** Can the user complete this booking? */
  canComplete: boolean;
  /** Should this booking be rendered with greyed-out styling? */
  isGreyedOut: boolean;
  /** Message to display for greyed-out bookings */
  greyedOutMessage?: string;
}

/**
 * Get comprehensive visibility details for a booking based on user role.
 * This is the centralized function for all booking visibility decisions.
 *
 * Spec:
 * - Club member: Full details for own bookings and, when enabled in organization
 *   settings, other members' bookings. Otherwise others are greyed out.
 * - Student: Own + instructor booking visible.
 * - CFI: Own + assigned student bookings visible.
 * - Admin/Dispatch: All visible.
 *
 * @param assignedStudentIds - Optional list of student IDs assigned to CFI
 */
export function getBookingVisibilityDetails(
  user: User | null,
  booking: Booking | null,
  org: Organization | null,
  assignedStudentIds?: string[]
): BookingVisibilityDetails {
  // Default: no access
  const noAccess: BookingVisibilityDetails = {
    canView: false,
    showFullDetails: false,
    canEdit: false,
    canCancel: false,
    canComplete: false,
    isGreyedOut: false,
  };

  if (!user || !booking || !org) return noAccess;

  const { uid } = user;
  const isOwnBooking = booking.userId === uid || booking.pilotId === uid;
  const showMemberNames = org.settings?.bookingVisibility?.showMemberNames !== false;

  const isFutureBooking = booking.start > new Date();
  const isPastBooking = booking.end < new Date();
  const isCompleted = booking.status === 'completed';
  const isCancelled = booking.status === 'cancelled';
  const canCancelByTiming = isFutureBooking && !isCompleted && !isCancelled;
  const canCompleteByTiming = isPastBooking && !isCompleted && !isCancelled;

  const perms = getRolePermissions(org.type as OrganizationType, user.role as UserRole);
  const hasDispatchOverride = perms.includes('BOOKINGS:DISPATCH_OVERRIDE');
  const hasCancelOthers = perms.includes('BOOKINGS:CANCEL_OTHERS');
  const hasUpdate = perms.includes('BOOKINGS:UPDATE');
  const hasDelete = perms.includes('BOOKINGS:DELETE');
  const hasAircraftUpdate = perms.includes('AIRCRAFT:UPDATE');

  // DISPATCH_OVERRIDE roles (admin, dispatch): full control over all bookings
  if (hasDispatchOverride) {
    return {
      canView: true,
      showFullDetails: true,
      canEdit: true,
      canCancel: canCancelByTiming,
      canComplete: canCompleteByTiming,
      isGreyedOut: false,
    };
  }

  // Maintenance: can view all for coordination, own-only edit/cancel
  if (hasAircraftUpdate && !hasUpdate) {
    return {
      canView: true,
      showFullDetails: isOwnBooking || showMemberNames,
      canEdit: isOwnBooking && !isPastBooking,
      canCancel: isOwnBooking && canCancelByTiming,
      canComplete: isOwnBooking && canCompleteByTiming,
      isGreyedOut: !isOwnBooking && !showMemberNames,
    };
  }

  // CANCEL_OTHERS + UPDATE (CFI in school): involved in training flights
  if (hasCancelOthers && hasUpdate) {
    const isInstructorBooking = booking.instructorId === uid;
    const isStudentBooking = booking.studentId && assignedStudentIds?.includes(booking.studentId);
    const isPilotStudentBooking = booking.pilotId && assignedStudentIds?.includes(booking.pilotId);
    const isInvolved = isOwnBooking || isInstructorBooking || isStudentBooking || isPilotStudentBooking;

    if (isInvolved) {
      return {
        canView: true,
        showFullDetails: true,
        canEdit: (isOwnBooking || isInstructorBooking) && !isPastBooking,
        canCancel: canCancelByTiming && isOwnBooking,
        canComplete: (isOwnBooking || isInstructorBooking) && canCompleteByTiming,
        isGreyedOut: false,
      };
    }
    return {
      canView: true,
      showFullDetails: false,
      canEdit: false,
      canCancel: false,
      canComplete: false,
      isGreyedOut: true,
      greyedOutMessage: 'Booking details hidden - not involved in this flight',
    };
  }

  // UPDATE + DELETE (member): own bookings with full control. Club admins can
  // opt into sharing other members' booking details through showMemberNames.
  if (hasUpdate) {
    if (isOwnBooking || (org.type === 'club' && showMemberNames)) {
      return {
        canView: true,
        showFullDetails: true,
        canEdit: isOwnBooking && !isPastBooking,
        canCancel: isOwnBooking && canCancelByTiming,
        canComplete: isOwnBooking && canCompleteByTiming,
        isGreyedOut: false,
      };
    }
    return {
      canView: true,
      showFullDetails: false,
      canEdit: false,
      canCancel: false,
      canComplete: false,
      isGreyedOut: true,
      greyedOutMessage: 'Booking details hidden - not your booking',
    };
  }

  // READ-only (student): own training flights only
  if (perms.includes('BOOKINGS:READ')) {
    const isStudentOwn = isOwnBooking || booking.studentId === uid;
    const isStudentInTraining = booking.type === 'training' && (booking.studentId === uid || booking.pilotId === uid);

    if (isStudentOwn || isStudentInTraining) {
      return {
        canView: true,
        showFullDetails: true,
        canEdit: false,
        canCancel: false,
        canComplete: false,
        isGreyedOut: false,
      };
    }
    return {
      canView: false,
      showFullDetails: false,
      canEdit: false,
      canCancel: false,
      canComplete: false,
      isGreyedOut: true,
      greyedOutMessage: 'Booking details hidden',
    };
  }

  return noAccess;
}

/**
 * Check if a booking should be visible in lists/calendars for a given user.
 * This is used for filtering bookings before rendering.
 *
 * @param assignedStudentIds - Optional list of student IDs assigned to CFI
 */
export function shouldShowBooking(
  user: User | null,
  booking: Booking | null,
  org: Organization | null,
  assignedStudentIds?: string[]
): boolean {
  const visibility = getBookingVisibilityDetails(user, booking, org, assignedStudentIds);
  return visibility.canView;
}

/**
 * Get a user-friendly message for greyed-out bookings based on role.
 */
export function getGreyedOutMessage(role: string): string {
  switch (role) {
    case 'member':
      return 'Booking details hidden - not your booking';
    case 'cfi':
      return 'Booking details hidden - not involved in this flight';
    case 'student':
      return 'Booking details hidden';
    default:
      return 'Booking details hidden';
  }
}

/**
 * Get all booking-related permissions for a user in an organization context.
 * This is useful for debugging and permission auditing.
 */
export function getBookingPermissions(user: User | null, org: Organization | null) {
  if (!user || !org) {
    return { canCreate: false, canViewAny: false, canEditOwn: false, canEditAny: false, canCancelOwn: false, canCancelAny: false, canCompleteOwn: false, canCompleteAny: false };
  }
  const perms = getRolePermissions(org.type as OrganizationType, user.role as UserRole);
  const hasOverride = perms.includes('BOOKINGS:DISPATCH_OVERRIDE');
  const hasCancelOthers = perms.includes('BOOKINGS:CANCEL_OTHERS');
  return {
    canCreate: perms.includes('BOOKINGS:CREATE'),
    canViewAny: hasOverride || hasCancelOthers || perms.includes('AIRCRAFT:UPDATE'),
    canEditOwn: perms.includes('BOOKINGS:UPDATE'),
    canEditAny: hasOverride,
    canCancelOwn: perms.includes('BOOKINGS:DELETE') || hasCancelOthers,
    canCancelAny: hasCancelOthers,
    canCompleteOwn: perms.includes('BOOKINGS:UPDATE') || user.role === 'student',
    canCompleteAny: hasOverride,
  };
}

// ===== STRUCTURED-RESULT WRAPPERS (optional, for auditing/debugging) =====
// These reuse the boolean rules above and add a reason + audit log line.
// Existing callers should keep using the boolean functions.

function bookingContext(user: User | null, booking: Booking | null, org: Organization | null) {
  return {
    userId: user?.uid,
    userRole: user?.role,
    orgType: org?.type,
    orgId: org?.id,
    bookingId: booking?.id,
  };
}

export function explainCreateBooking(user: User | null, org: Organization | null): PermissionResult {
  const allowed = canCreateBooking(user, org);
  return explain(
    'createBooking',
    allowed,
    allowed ? `Role ${user?.role} may create bookings` : `Role ${user?.role ?? 'unknown'} may not create bookings`,
    { userId: user?.uid, userRole: user?.role, orgType: org?.type, orgId: org?.id }
  );
}

export function explainEditBooking(user: User | null, booking: Booking | null, org: Organization | null): PermissionResult {
  const allowed = canEditBooking(user, booking, org);
  return explain(
    'editBooking',
    allowed,
    allowed ? `Role ${user?.role} may edit this booking` : `Role ${user?.role ?? 'unknown'} may not edit this booking`,
    bookingContext(user, booking, org)
  );
}

export function explainCancelBooking(user: User | null, booking: Booking | null, org: Organization | null): PermissionResult {
  const allowed = canCancelBooking(user, booking, org);
  return explain(
    'cancelBooking',
    allowed,
    allowed ? `Role ${user?.role} may cancel this booking` : `Role ${user?.role ?? 'unknown'} may not cancel this booking`,
    bookingContext(user, booking, org)
  );
}

export function explainCompleteBooking(user: User | null, booking: Booking | null, org: Organization | null): PermissionResult {
  const allowed = canCompleteBooking(user, booking, org);
  return explain(
    'completeBooking',
    allowed,
    allowed ? `Role ${user?.role} may complete this booking` : `Role ${user?.role ?? 'unknown'} may not complete this booking`,
    bookingContext(user, booking, org)
  );
}

export function explainViewBooking(user: User | null, booking: Booking | null, org: Organization | null): PermissionResult {
  const allowed = canViewBooking(user, booking, org);
  return explain(
    'viewBooking',
    allowed,
    allowed ? `Role ${user?.role} may view this booking` : `Role ${user?.role ?? 'unknown'} may not view this booking`,
    bookingContext(user, booking, org)
  );
}
